import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  Paper,
  Typography,
  Select,
  MenuItem,
  Button,
  Stack,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  TextField,
  Divider,
} from "@mui/material";
import {
  DragIndicator as DragIndicatorIcon,
  Close as CloseIcon,
  TableChart as TableChartIcon,
} from "@mui/icons-material";

const GENERIC_INPUT_TEXT_SX = {
  "& .MuiInputBase-input::placeholder": {
    color: "text.secondary",
    opacity: 1,
  },
  "& .MuiFormHelperText-root:not(.Mui-error)": {
    color: "text.secondary",
  },
};

const getPopoverFieldKey = (field) =>
  field?.field_instance_id || field?.id || field?.name;

const PaisTagAssignPopover = ({
  open,
  categoryData = [],
  selectedCount = 0,
  detectedColumnCount = 0,
  detectedRowCount = 0,
  columnDefinitions = [],
  savedColumnOrder = [],
  supportsPaisTableTagging = true,
  supportsGenericTableTagging = true,
  editingGenericTable = null,
  pdfPreview = null,
  onAssign,
  onClose,
}) => {
  const [tableType, setTableType] = useState("pais");
  const [categoryCode, setCategoryCode] = useState("");
  const [orderedColumns, setOrderedColumns] = useState([]);
  const [orderedSubCategories, setOrderedSubCategories] = useState([]);
  const [genericLabel, setGenericLabel] = useState("");
  const [genericRowMode, setGenericRowMode] = useState("fixed");
  const [genericHeaderMode, setGenericHeaderMode] = useState("named_rows");
  const [genericCustomRowCount, setGenericCustomRowCount] = useState("");
  const [genericColumnNames, setGenericColumnNames] = useState([]);
  const [genericColumnTypes, setGenericColumnTypes] = useState([]);
  const [genericColumnTotalSources, setGenericColumnTotalSources] = useState(
    [],
  );
  const [genericRowNames, setGenericRowNames] = useState([]);
  const [genericMemberRows, setGenericMemberRows] = useState([]);
  const [genericKeptColumnIndices, setGenericKeptColumnIndices] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragType, setDragType] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [mobilePane, setMobilePane] = useState("setup");
  const scrollContainerRef = React.useRef(null);

  const AUTO_SCROLL_EDGE = 48;
  const AUTO_SCROLL_SPEED = 12;

  const handleBodyDragOver = (e) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { top, bottom } = container.getBoundingClientRect();
    const cursorY = e.clientY;

    if (cursorY < top + AUTO_SCROLL_EDGE) {
      container.scrollTop -= AUTO_SCROLL_SPEED;
    } else if (cursorY > bottom - AUTO_SCROLL_EDGE) {
      container.scrollTop += AUTO_SCROLL_SPEED;
    }
  };

  const visibleColumnDefinitions = useMemo(
    () => columnDefinitions.slice(0, detectedColumnCount),
    [columnDefinitions, detectedColumnCount],
  );

  const initialColumnOrder = useMemo(() => {
    const isCompatibleSavedOrder =
      savedColumnOrder.length === visibleColumnDefinitions.length &&
      savedColumnOrder.every((column) => column?.label && column?.code);

    return isCompatibleSavedOrder ? savedColumnOrder : visibleColumnDefinitions;
  }, [savedColumnOrder, visibleColumnDefinitions]);

  const resolvedDetectedColumnCount = Math.max(1, detectedColumnCount || 1);
  const resolvedDetectedRowCount = Math.max(
    1,
    detectedRowCount ||
      Math.ceil(selectedCount / resolvedDetectedColumnCount) ||
      1,
  );
  const isEditingGenericTable = Boolean(editingGenericTable?.tableId);
  const genericColumnCount = isEditingGenericTable
    ? genericKeptColumnIndices.length
    : resolvedDetectedColumnCount;
  const parsedCustomRowCount = Number(genericCustomRowCount);
  const genericRowCount = isEditingGenericTable
    ? genericMemberRows.length
    : resolvedDetectedRowCount;
  const minimumRequiredRowCount = Math.max(genericRowCount, 1);
  const hasNamedRows = genericHeaderMode === "named_rows";

  const resizeNameList = (currentNames, count) =>
    Array.from({ length: count }, (_, index) => currentNames[index] || "");

  useEffect(() => {
    if (!open) return;
    setOrderedColumns(initialColumnOrder);
    setDragIndex(null);
    setMobilePane("setup");
  }, [open, initialColumnOrder]);

  useEffect(() => {
    if (!open) return;

    if (isEditingGenericTable) {
      const existingConfig = editingGenericTable.config || {};
      const existingRows = editingGenericTable.rows || [];

      setGenericKeptColumnIndices(
        Array.from(
          { length: resolvedDetectedColumnCount },
          (_, index) => index,
        ),
      );

      setGenericLabel(existingConfig.label || "");
      setGenericRowMode(existingConfig.rowMode || "fixed");
      setGenericHeaderMode(existingConfig.headerMode || "named_rows");
      setGenericCustomRowCount(
        existingConfig.maxRowCount === null ||
          existingConfig.maxRowCount === undefined
          ? ""
          : String(existingConfig.maxRowCount),
      );
      setGenericColumnNames(
        resizeNameList(
          existingConfig.columnNames || [],
          resolvedDetectedColumnCount,
        ),
      );
      setGenericColumnTypes(
        Array.from(
          { length: resolvedDetectedColumnCount },
          (_, index) => existingConfig.columnTypes?.[index] || "text",
        ),
      );
      setGenericColumnTotalSources(
        Array.from({ length: resolvedDetectedColumnCount }, (_, index) =>
          Array.isArray(existingConfig.columnTotalSources?.[index])
            ? [...existingConfig.columnTotalSources[index]]
            : [],
        ),
      );
      setGenericRowNames(
        resizeNameList(existingConfig.rowNames || [], existingRows.length),
      );
      setGenericMemberRows(
        existingRows.map((row, rowIndex) => {
          const memberFieldKeys = (row.fields || [])
            .map(getPopoverFieldKey)
            .filter(Boolean);

          return {
            id: `generic_row_${rowIndex}_${memberFieldKeys.join("_")}`,
            memberFieldKeys,
            isNew: (row.fields || []).some(
              (field) =>
                field?.generic_table?.table_id !== editingGenericTable.tableId,
            ),
          };
        }),
      );
      return;
    }

    setGenericColumnNames((currentNames) =>
      resizeNameList(currentNames, resolvedDetectedColumnCount),
    );
    setGenericColumnTypes((currentTypes) =>
      Array.from(
        { length: resolvedDetectedColumnCount },
        (_, index) => currentTypes[index] || "text",
      ),
    );
    setGenericRowNames((currentNames) =>
      resizeNameList(currentNames, resolvedDetectedRowCount),
    );
    setGenericColumnTotalSources((currentSources) =>
      Array.from({ length: resolvedDetectedColumnCount }, (_, index) =>
        (currentSources[index] || []).filter(
          (sourceIndex) => sourceIndex < resolvedDetectedColumnCount,
        ),
      ),
    );
    setGenericMemberRows([]);
  }, [
    editingGenericTable,
    isEditingGenericTable,
    open,
    resolvedDetectedColumnCount,
    resolvedDetectedRowCount,
  ]);

  useEffect(() => {
    if (!open) return;

    if (
      isEditingGenericTable ||
      (!supportsPaisTableTagging && supportsGenericTableTagging)
    ) {
      setTableType("generic");
      return;
    }

    if (supportsPaisTableTagging) {
      setTableType("pais");
    }
  }, [
    isEditingGenericTable,
    open,
    supportsGenericTableTagging,
    supportsPaisTableTagging,
  ]);

  const selectedCategory = categoryData.find(
    (c) => c.categoryCode === categoryCode,
  );

  useEffect(() => {
    if (!open || !selectedCategory) {
      setOrderedSubCategories([]);
      return;
    }

    setOrderedSubCategories(selectedCategory.subCategory || []);
  }, [open, selectedCategory]);

  const handleRemoveGenericColumn = (columnIndex) => {
    if (!isEditingGenericTable || genericColumnCount <= 1) return;

    const originalColumnIndex = genericKeptColumnIndices[columnIndex];
    const removedFieldKeys = new Set();

    (editingGenericTable.rows || []).forEach((row) => {
      row.fields.forEach((field, fieldIndex) => {
        const sourceColumnIndex =
          editingGenericTable.columnIndexByFieldKey?.get(
            getPopoverFieldKey(field),
          ) ?? fieldIndex;

        if (sourceColumnIndex === originalColumnIndex) {
          removedFieldKeys.add(getPopoverFieldKey(field));
        }
      });
    });

    const remainingRows = [];
    const remainingRowNames = [];

    genericMemberRows.forEach((row, rowIndex) => {
      const memberFieldKeys = row.memberFieldKeys.filter(
        (key) => !removedFieldKeys.has(key),
      );

      // A row with no remaining widgets cannot stay in the table.
      if (!memberFieldKeys.length) return;

      remainingRows.push({ ...row, memberFieldKeys });
      remainingRowNames.push(genericRowNames[rowIndex] || "");
    });

    if (!remainingRows.length) return;

    setGenericKeptColumnIndices((indices) =>
      indices.filter((_, index) => index !== columnIndex),
    );
    setGenericColumnNames((names) =>
      names.filter((_, index) => index !== columnIndex),
    );
    setGenericColumnTypes((types) =>
      types.filter((_, index) => index !== columnIndex),
    );
    setGenericColumnTotalSources((columns) =>
      columns
        .filter((_, index) => index !== columnIndex)
        .map((sources) =>
          sources
            .filter((index) => index !== columnIndex)
            .map((index) => (index > columnIndex ? index - 1 : index)),
        ),
    );
    setGenericMemberRows(remainingRows);
    setGenericRowNames(remainingRowNames);
  };

  const handleGenericColumnTypeChange = (columnIndex, nextType) => {
    setGenericColumnTypes((types) =>
      types.map((type, index) => (index === columnIndex ? nextType : type)),
    );
    setGenericColumnTotalSources((columns) =>
      columns.map((sources, index) => {
        if (index === columnIndex && nextType !== "total") return [];

        return nextType === "total"
          ? sources.filter((sourceIndex) => sourceIndex !== columnIndex)
          : sources;
      }),
    );
  };

  const genericColumnTotalSourcesValid =
    genericColumnTotalSources.length === genericColumnCount &&
    genericColumnTypes.every((type, columnIndex) => {
      if (type !== "total") return true;

      const sources = genericColumnTotalSources[columnIndex];
      return (
        Array.isArray(sources) &&
        sources.length > 0 &&
        new Set(sources).size === sources.length &&
        sources.every(
          (index) =>
            Number.isInteger(index) &&
            index >= 0 &&
            index < genericColumnCount &&
            index !== columnIndex &&
            genericColumnTypes[index] !== "total",
        )
      );
    });

  if (!open) return null;

  const handleAssign = () => {
    if (tableType === "pais") {
      if (!selectedCategory) return;
      onAssign?.({
        category: selectedCategory,
        columnOrder: orderedColumns,
        subCategoryOrder: orderedSubCategories,
      });
    } else {
      const normalizedColumnNames = genericColumnNames.map((name) =>
        name.trim(),
      );
      const normalizedColumnTypes = genericColumnTypes.map((type) =>
        type.trim(),
      );
      const normalizedRowNames = hasNamedRows
        ? genericRowNames.map((name) => name.trim())
        : [];
      const isValidCustomRowCount =
        genericRowMode !== "custom" ||
        genericCustomRowCount === "" ||
        (Number.isInteger(parsedCustomRowCount) &&
          parsedCustomRowCount >= minimumRequiredRowCount);
      const isValidGenericConfiguration =
        genericLabel.trim() &&
        genericRowCount > 0 &&
        isValidCustomRowCount &&
        normalizedColumnNames.length === genericColumnCount &&
        normalizedColumnNames.every(Boolean) &&
        normalizedColumnTypes.length === genericColumnCount &&
        normalizedColumnTypes.every((type) =>
          ["text", "number", "total"].includes(type),
        ) &&
        genericColumnTotalSourcesValid &&
        (!hasNamedRows ||
          (normalizedRowNames.length === genericRowCount &&
            normalizedRowNames.every(Boolean)));

      if (!isValidGenericConfiguration) return;

      onAssign?.({
        tableType: "generic",
        ...(isEditingGenericTable
          ? {
              memberFieldKeys: genericMemberRows.flatMap(
                (row) => row.memberFieldKeys,
              ),
            }
          : {}),
        genericConfig: {
          label: genericLabel.trim(),
          rowMode: genericRowMode,
          headerMode: genericHeaderMode,
          rowCount: genericRowCount,
          maxRowCount:
            genericRowMode === "custom" && genericCustomRowCount !== ""
              ? parsedCustomRowCount
              : null,
          columnCount: genericColumnCount,
          ...(isEditingGenericTable
            ? { keptColumnIndices: genericKeptColumnIndices }
            : {}),
          rowNames: normalizedRowNames,
          columnNames: normalizedColumnNames,
          columnTypes: normalizedColumnTypes,
          columnTotalSources: genericColumnTotalSources.map((sources, index) =>
            normalizedColumnTypes[index] === "total" ? [...sources] : [],
          ),
        },
      });
    }

    setCategoryCode("");
    setOrderedColumns([]);
    setOrderedSubCategories([]);
    setGenericLabel("");
    setGenericRowMode("fixed");
    setGenericHeaderMode("named_rows");
    setGenericCustomRowCount("");
    setGenericColumnNames([]);
    setGenericColumnTypes([]);
    setGenericColumnTotalSources([]);
    setGenericRowNames([]);
    setGenericMemberRows([]);
    setGenericKeptColumnIndices([]);
    onClose?.();
  };

  const handleClose = () => {
    setTableType("pais");
    setCategoryCode("");
    setOrderedColumns([]);
    setOrderedSubCategories([]);
    setGenericLabel("");
    setGenericRowMode("fixed");
    setGenericHeaderMode("named_rows");
    setGenericCustomRowCount("");
    setGenericColumnNames([]);
    setGenericColumnTypes([]);
    setGenericColumnTotalSources([]);
    setGenericRowNames([]);
    setGenericMemberRows([]);
    setGenericKeptColumnIndices([]);
    setDragIndex(null);
    setDragType(null);
    setDragOverIndex(null);
    onClose?.();
  };

  const handleDropReorder = (targetIndex, type) => {
    if (dragIndex === null || dragIndex === targetIndex || dragType !== type) {
      setDragIndex(null);
      setDragType(null);
      setDragOverIndex(null);
      return;
    }

    const swap = (prev) => {
      const next = [...prev];
      [next[dragIndex], next[targetIndex]] = [
        next[targetIndex],
        next[dragIndex],
      ];
      return next;
    };

    if (type === "column") {
      setOrderedColumns(swap);
    }

    if (type === "subcategory") {
      setOrderedSubCategories(swap);
    }

    setDragIndex(null);
    setDragType(null);
    setDragOverIndex(null);
  };

  const renderDraggableList = (items, type) =>
    items.map((item, index) => {
      const isDragging = dragType === type && dragIndex === index;
      const isDragOver =
        dragType === type && dragOverIndex === index && dragIndex !== index;

      return (
        <Box
          key={`${type}_${item.code}_${item.label}`}
          onDragOver={(e) => {
            e.preventDefault();
            if (dragType === type) setDragOverIndex(index);
          }}
          onDragLeave={() =>
            setDragOverIndex((prev) => (prev === index ? null : prev))
          }
          onDrop={() => handleDropReorder(index, type)}
          draggable
          onDragStart={() => {
            setDragIndex(index);
            setDragType(type);
          }}
          onDragEnd={() => {
            setDragIndex(null);
            setDragType(null);
            setDragOverIndex(null);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            border: "1px solid",
            borderColor: isDragOver ? "primary.main" : "#e2e5e9",
            borderRadius: 1.5,
            px: 1,
            py: 0.875,
            backgroundColor: isDragging ? "rgba(25,118,210,0.06)" : "#fff",
            opacity: isDragging ? 0.5 : 1,
            cursor: "grab",
            transition: "border-color 0.12s ease, background-color 0.12s ease",
            "&:active": { cursor: "grabbing" },
            "&:hover": {
              borderColor: "primary.light",
              backgroundColor: "rgba(25,118,210,0.03)",
            },
          }}
        >
          <DragIndicatorIcon
            fontSize="small"
            sx={{ color: "text.disabled", flexShrink: 0 }}
          />
          <Chip
            label={
              type === "column" ? String.fromCharCode(65 + index) : index + 1
            }
            size="small"
            sx={{
              minWidth: 28,
              height: 22,
              fontWeight: 700,
              fontSize: "0.7rem",
              textTransform: "lowercase",
              backgroundColor: "rgba(25,118,210,0.1)",
              color: "primary.main",
            }}
          />
          <Typography variant="body2" sx={{ flex: 1 }}>
            {item.label}
          </Typography>
        </Box>
      );
    });

  const genericColumnNamesValid =
    genericColumnCount > 0 &&
    genericColumnNames.length === genericColumnCount &&
    genericColumnNames.every((name) => name.trim());

  const genericColumnTypesValid =
    genericColumnTypes.length === genericColumnCount &&
    genericColumnTypes.every((type) =>
      ["text", "number", "total"].includes(type),
    );
  const genericRowNamesValid =
    !hasNamedRows ||
    (genericRowNames.length === genericRowCount &&
      genericRowNames.every((name) => name.trim()));
  const genericCustomRowCountValid =
    genericRowMode !== "custom" ||
    genericCustomRowCount === "" ||
    (Number.isInteger(parsedCustomRowCount) &&
      parsedCustomRowCount >= minimumRequiredRowCount);
  const canAssignGeneric = Boolean(
    genericLabel.trim() &&
      genericRowCount > 0 &&
      genericColumnNamesValid &&
      genericColumnTypesValid &&
      genericColumnTotalSourcesValid &&
      genericRowNamesValid &&
      genericCustomRowCountValid,
  );

  return (
    <Box
      onClick={handleClose}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 10001,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(15, 23, 42, 0.45)",
        backdropFilter: "blur(1px)",
        p: 2,
      }}
    >
      <Paper
        elevation={8}
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: pdfPreview ? 1100 : 400,
          maxWidth: "96vw",
          height: pdfPreview ? "82vh" : "auto",
          maxHeight: "82vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            px: 2.5,
            py: 2,
            backgroundColor: "#fafbfc",
            borderBottom: "1px solid #eef0f2",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(25,118,210,0.1)",
                color: "primary.main",
                flexShrink: 0,
              }}
            >
              <TableChartIcon fontSize="small" />
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, lineHeight: 1.2 }}
              >
                {isEditingGenericTable
                  ? "Edit Generic Table"
                  : "Tag as Table Fields"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isEditingGenericTable
                  ? `${genericRowCount} table row${genericRowCount === 1 ? "" : "s"}`
                  : `${selectedCount} field${selectedCount === 1 ? "" : "s"} selected`}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {pdfPreview && (
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              gap: 0.5,
              p: 0.75,
              backgroundColor: "#f4f6f8",
              borderBottom: "1px solid #eef0f2",
            }}
          >
            {[
              { value: "setup", label: "Table setup" },
              { value: "preview", label: "PDF reference" },
            ].map((pane) => {
              const isActive = mobilePane === pane.value;

              return (
                <ButtonBase
                  key={pane.value}
                  onClick={() => setMobilePane(pane.value)}
                  sx={{
                    flex: 1,
                    py: 0.75,
                    borderRadius: 1.5,
                    backgroundColor: isActive ? "#fff" : "transparent",
                    color: isActive ? "text.primary" : "text.secondary",
                    boxShadow: isActive
                      ? "0 1px 3px rgba(15, 23, 42, 0.14)"
                      : "none",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                  }}
                >
                  {pane.label}
                </ButtonBase>
              );
            })}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {pdfPreview && (
            <Box
              sx={{
                display: {
                  xs: mobilePane === "preview" ? "flex" : "none",
                  md: "flex",
                },
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                overflow: "hidden",
                borderLeft: { xs: 0, md: "1px solid #eef0f2" },
                backgroundColor: "#e9edf2",
              }}
            >
              {pdfPreview}
            </Box>
          )}
          {pdfPreview && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Divider orientation="vertical" flexItem sx={{ mr: 2 }} />
            </Box>
          )}
          {/* Body */}
          <Box
            ref={scrollContainerRef}
            onDragOver={handleBodyDragOver}
            sx={{
              display: {
                xs: !pdfPreview || mobilePane === "setup" ? "flex" : "none",
                md: "flex",
              },
              flex: pdfPreview ? "0 0 430px" : 1,
              width: { xs: "100%", md: pdfPreview ? 430 : "100%" },
              minWidth: 0,
              flexDirection: "column",
              gap: 2.25,
              overflowY: "auto",
              px: 2.5,
              py: 2.25,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#d0d5dc",
                borderRadius: 3,
              },
            }}
          >
            <Stack spacing={0.75}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Table Type
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  p: 0.5,
                  borderRadius: 999,
                  backgroundColor: "#eceff3",
                  gap: 0.5,
                  width: "fit-content",
                }}
              >
                {[
                  {
                    value: "pais",
                    label: "PAIS",
                    disabled:
                      isEditingGenericTable || !supportsPaisTableTagging,
                  },
                  { value: "generic", label: "GENERIC" },
                ].map((option) => {
                  const isActive = tableType === option.value;

                  return (
                    <ButtonBase
                      key={option.value}
                      onClick={() => {
                        if (!option.disabled) {
                          setTableType(option.value);
                        }
                      }}
                      sx={{
                        px: 2,
                        py: 0.8,
                        borderRadius: 999,
                        minWidth: 88,
                        backgroundColor: isActive ? "#fff" : "transparent",
                        boxShadow: isActive
                          ? "0 1px 3px rgba(15, 23, 42, 0.18)"
                          : "none",
                        color: option.disabled
                          ? "text.disabled"
                          : isActive
                            ? "text.primary"
                            : "text.secondary",
                        opacity: option.disabled ? 0.55 : 1,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        letterSpacing: 0.3,
                        cursor: option.disabled ? "not-allowed" : "pointer",
                        transition:
                          "background-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease",
                      }}
                    >
                      {option.label}
                    </ButtonBase>
                  );
                })}
              </Box>
            </Stack>

            {tableType === "pais" && (
              <>
                <FormControl size="small" fullWidth>
                  <InputLabel
                    id="pais-category-select-label"
                    sx={{
                      px: 0.5,
                      bgcolor: "#fff",
                      color: "text.secondary",
                      "&.Mui-focused": {
                        color: "#424242",
                      },
                    }}
                  >
                    Category
                  </InputLabel>
                  <Select
                    labelId="pais-category-select-label"
                    label="Category"
                    value={categoryCode}
                    onChange={(e) => setCategoryCode(e.target.value)}
                  >
                    {categoryData.map((cat) => (
                      <MenuItem key={cat.categoryCode} value={cat.categoryCode}>
                        {cat.categoryLabel}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {!!visibleColumnDefinitions.length && (
                  <Stack spacing={0.75}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Column Mapping
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Drag to reorder
                      </Typography>
                    </Stack>
                    {renderDraggableList(orderedColumns, "column")}
                  </Stack>
                )}

                {!!orderedSubCategories.length && (
                  <Stack spacing={0.75}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Subcategory Mapping
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Drag to reorder
                      </Typography>
                    </Stack>
                    {renderDraggableList(orderedSubCategories, "subcategory")}
                  </Stack>
                )}

                {!categoryCode && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ pt: 0.5 }}
                  >
                    Select a category above to map columns and subcategories.
                  </Typography>
                )}
              </>
            )}

            {tableType === "generic" && (
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Table name"
                  value={genericLabel}
                  onChange={(e) => setGenericLabel(e.target.value)}
                  placeholder="Example: Schedule of values"
                  sx={GENERIC_INPUT_TEXT_SX}
                />

                <FormControl size="small" fullWidth>
                  <InputLabel
                    id="generic-row-mode-select-label"
                    sx={{
                      px: 0.5,
                      bgcolor: "#fff",
                      color: "text.secondary",
                      "&.Mui-focused": {
                        color: "#424242",
                      },
                    }}
                  >
                    Number of rows
                  </InputLabel>
                  <Select
                    labelId="generic-row-mode-select-label"
                    label="Number of rows"
                    value={genericRowMode}
                    onChange={(e) => {
                      const nextRowMode = e.target.value;
                      setGenericRowMode(nextRowMode);

                      if (nextRowMode === "custom") {
                        setGenericHeaderMode("column_names");
                      }
                    }}
                  >
                    <MenuItem value="fixed">
                      Fixed ({genericRowCount} detected row
                      {genericRowCount === 1 ? "" : "s"})
                    </MenuItem>
                    <MenuItem value="custom">Custom number of rows</MenuItem>
                  </Select>
                </FormControl>

                {genericRowMode === "custom" && (
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Maximum rows (optional)"
                    value={genericCustomRowCount}
                    onChange={(e) => setGenericCustomRowCount(e.target.value)}
                    inputProps={{ min: minimumRequiredRowCount, step: 1 }}
                    error={
                      genericCustomRowCount !== "" &&
                      !genericCustomRowCountValid
                    }
                    helperText={
                      genericCustomRowCount === ""
                        ? `Leave blank to allow unlimited rows. The preview starts with ${genericRowCount} row${genericRowCount === 1 ? "" : "s"}.`
                        : `Maximum must be at least ${minimumRequiredRowCount} row${minimumRequiredRowCount === 1 ? "" : "s"} to cover the selected PDF fields.`
                    }
                    sx={GENERIC_INPUT_TEXT_SX}
                  />
                )}

                <FormControl size="small" fullWidth>
                  <InputLabel
                    id="generic-header-mode-select-label"
                    sx={{
                      px: 0.5,
                      bgcolor: "#fff",
                      color: "text.secondary",
                      "&.Mui-focused": {
                        color: "#424242",
                      },
                    }}
                  >
                    Headers
                  </InputLabel>
                  <Select
                    labelId="generic-header-mode-select-label"
                    label="Headers"
                    value={genericHeaderMode}
                    onChange={(e) => setGenericHeaderMode(e.target.value)}
                    disabled={genericRowMode === "custom"}
                  >
                    <MenuItem value="named_rows">Row and column names</MenuItem>
                    <MenuItem value="column_names">Column names only</MenuItem>
                  </Select>
                </FormControl>

                {genericRowMode === "custom" && (
                  <Typography variant="caption" color="text.secondary">
                    Custom tables use column names only because rows can be
                    added or removed in the preview.
                  </Typography>
                )}

                <Stack spacing={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Column names
                  </Typography>
                  {genericColumnNames.map((name, index) => (
                    <React.Fragment key={`generic_column_${index}`}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                      >
                        <TextField
                          fullWidth
                          required
                          size="small"
                          label={`Column ${index + 1}`}
                          value={name}
                          onChange={(e) =>
                            setGenericColumnNames((currentNames) =>
                              currentNames.map((currentName, currentIndex) =>
                                currentIndex === index
                                  ? e.target.value
                                  : currentName,
                              ),
                            )
                          }
                          placeholder={`Enter column ${index + 1} name`}
                          sx={GENERIC_INPUT_TEXT_SX}
                        />
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel id={`generic-column-type-${index}-label`}>
                            Field type
                          </InputLabel>
                          <Select
                            labelId={`generic-column-type-${index}-label`}
                            label="Field type"
                            value={genericColumnTypes[index] || "text"}
                            onChange={(e) =>
                              handleGenericColumnTypeChange(
                                index,
                                e.target.value,
                              )
                            }
                          >
                            <MenuItem value="text">Text</MenuItem>
                            <MenuItem value="number">Number</MenuItem>
                            <MenuItem value="total">Total</MenuItem>
                          </Select>
                        </FormControl>
                        {isEditingGenericTable && (
                          <Button
                            size="small"
                            color="error"
                            disabled={genericColumnCount <= 1}
                            onClick={() => handleRemoveGenericColumn(index)}
                            sx={{
                              textTransform: "none",
                              flexShrink: 0,
                              alignSelf: "center",
                            }}
                          >
                            Remove
                          </Button>
                        )}
                      </Stack>
                      {genericColumnTypes[index] === "total" && (
                        <TextField
                          select
                          fullWidth
                          required
                          size="small"
                          label={`Columns to sum for ${name.trim() || `Column ${index + 1}`}`}
                          value={genericColumnTotalSources[index] || []}
                          onChange={(event) => {
                            const value = event.target.value;
                            const sources =
                              typeof value === "string"
                                ? value.split(",").filter(Boolean).map(Number)
                                : value;
                            setGenericColumnTotalSources((columns) =>
                              columns.map((currentSources, columnIndex) =>
                                columnIndex === index
                                  ? sources
                                  : currentSources,
                              ),
                            );
                          }}
                          SelectProps={{
                            multiple: true,
                            renderValue: (sources) =>
                              sources
                                .map(
                                  (sourceIndex) =>
                                    genericColumnNames[sourceIndex]?.trim() ||
                                    `Column ${sourceIndex + 1}`,
                                )
                                .join(", "),
                          }}
                          error={!genericColumnTotalSources[index]?.length}
                          helperText={
                            !genericColumnTypes.some(
                              (type, sourceIndex) =>
                                sourceIndex !== index && type !== "total",
                            )
                              ? "Add or change another column to Text or Number to use it as a source."
                              : !genericColumnTotalSources[index]?.length
                                ? "Select at least one source column. Total columns cannot be sources."
                                : "Sums the selected columns in each row. Individual cell overrides are preserved."
                          }
                          sx={GENERIC_INPUT_TEXT_SX}
                        >
                          {genericColumnNames.map((sourceName, sourceIndex) =>
                            sourceIndex !== index &&
                            genericColumnTypes[sourceIndex] !== "total" ? (
                              <MenuItem key={sourceIndex} value={sourceIndex}>
                                {sourceName.trim() ||
                                  `Column ${sourceIndex + 1}`}
                              </MenuItem>
                            ) : null,
                          )}
                        </TextField>
                      )}
                    </React.Fragment>
                  ))}
                </Stack>

                {hasNamedRows && (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Row names
                    </Typography>
                    {genericRowNames.map((name, index) => (
                      <Box
                        key={`generic_row_${index}`}
                        display={"flex"}
                        flexDirection={"row"}
                      >
                        <TextField
                          fullWidth
                          required
                          size="small"
                          label={`Row ${index + 1}`}
                          value={name}
                          onChange={(e) =>
                            setGenericRowNames((currentNames) =>
                              currentNames.map((currentName, currentIndex) =>
                                currentIndex === index
                                  ? e.target.value
                                  : currentName,
                              ),
                            )
                          }
                          placeholder={`Enter row ${index + 1} name`}
                          sx={GENERIC_INPUT_TEXT_SX}
                        />
                        {isEditingGenericTable && (
                          <Button
                            size="small"
                            color="error"
                            disabled={genericMemberRows.length <= 1}
                            onClick={() => {
                              setGenericMemberRows((currentRows) =>
                                currentRows.filter(
                                  (_, currentIndex) => currentIndex !== index,
                                ),
                              );
                              setGenericRowNames((currentNames) =>
                                currentNames.filter(
                                  (_, currentIndex) => currentIndex !== index,
                                ),
                              );
                            }}
                            sx={{ textTransform: "none", flexShrink: 0 }}
                          >
                            Remove
                          </Button>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}

                <Typography variant="body2" color="text.secondary">
                  {genericRowCount} initial row
                  {genericRowCount === 1 ? "" : "s"} and {genericColumnCount}{" "}
                  column
                  {genericColumnCount === 1 ? "" : "s"} will be configured
                  {genericRowMode === "custom"
                    ? genericCustomRowCount === ""
                      ? ", with no row limit."
                      : `, with a maximum of ${parsedCustomRowCount} rows.`
                    : "."}
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            backgroundColor: "#fafbfc",
            borderTop: "1px solid #eef0f2",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              size="small"
              onClick={handleClose}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={
                tableType === "pais" ? !categoryCode : !canAssignGeneric
              }
              onClick={handleAssign}
              sx={{ textTransform: "none", px: 2.5 }}
            >
              {isEditingGenericTable ? "Update Table" : "Tag Selected Fields"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default PaisTagAssignPopover;
